import { useState } from 'react'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { PlansPlanMode } from '../../../api/types'
import { showAsFormattedRupeeAmount } from '../../../../display-values/lumpsumPlanAmount'
import type { LumpsumAllocationBuyPlanRecommendationDetails } from '../../api/allocationRecommendation.types'
import {
  areCounterFundsActionsCompleted,
  PlanRecommendationIntro
} from '../../shared/sheet/PlanRecommendationIntro'
import { PlanRecommendationSheetSkeleton } from '../../shared/sheet/PlanRecommendationSheetSkeleton'
import { PlansScreen } from '../../../../analytics/plansEvents'
import { PlanRecommendationSheetLayout } from '../../shared/sheet/PlanRecommendationSheetLayout'
import { PlanRecommendationStatusReasonBannerSection } from '../../shared/sheet/PlanRecommendationStatusReasonBannerSection'
import type { FundMissedGainsInfoParams } from '../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { AllocationBuyPlanRecommendationBody } from './body/AllocationBuyPlanRecommendationBody'
import { AllocationBuyPlanRecommendationHeader } from './header/AllocationBuyPlanRecommendationHeader'

type AllocationBuyPlanRecommendationSheetProps = {
  onDismiss: () => void
  isin: string
  planDetails: LumpsumAllocationBuyPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
  transactionCta: unknown
}

export function AllocationBuyPlanRecommendationSheet({
  onDismiss,
  isin,
  planDetails,
  isLoading,
  isFetchFailed,
  onRetry,
  onMissedGainsInfoClick,
  transactionCta
}: AllocationBuyPlanRecommendationSheetProps) {
  const [selectedSellIndex, setSelectedSellIndex] = useState(0)

  // Buy-entry: root totalTransferAmount is buy; other side = sum of rounded sell card amounts.
  const buyAmount = showAsFormattedRupeeAmount({
    rawAmount: planDetails?.totalTransferAmount ?? 0
  }).value
  const sellAmount = (planDetails?.fundsToBeSold ?? []).reduce(
    (sum, fund) =>
      sum + showAsFormattedRupeeAmount({ rawAmount: fund?.sellAmount ?? 0 }).value,
    0
  )

  return (
    <PlanRecommendationSheetLayout
      onDismiss={onDismiss}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      onRetry={onRetry}
      planMode={PlansPlanMode.ALLOCATION}
      analyticsScreen={PlansScreen.LumpsumAllocationRecommendation}
      fundName={planDetails?.fundName ?? ''}
      fundIsin={isin}
    >
      {!isLoading ? (
        <PlanRecommendationStatusReasonBannerSection planDetails={planDetails} analyticsScreen={PlansScreen.LumpsumAllocationRecommendation} />
      ) : null}
      {isLoading ? (
        <PlanRecommendationSheetSkeleton planMode={PlansPlanMode.ALLOCATION} detailsTitle="Buy details" />
      ) : (
        <>
          <PlanRecommendationIntro
            planMode={PlansPlanMode.ALLOCATION}
            buyAmount={buyAmount}
            sellAmount={sellAmount}
            areBuyActionsCompleted={planDetails?.executed === true}
            areSellActionsCompleted={areCounterFundsActionsCompleted(planDetails?.fundsToBeSold)}
            entryAction={LumpsumRecommendationActionType.BUY}
            counterFundCount={planDetails?.fundsToBeSold?.length ?? 0}
          />
          <AllocationBuyPlanRecommendationHeader
            planDetails={planDetails}
            entryIsin={isin}
            selectedSellIndex={selectedSellIndex}
            onSelectSellIndex={setSelectedSellIndex}
            transactionCta={transactionCta}
          />
          <AllocationBuyPlanRecommendationBody
            planDetails={planDetails}
            selectedSellIndex={selectedSellIndex}
            onSelectSellIndex={setSelectedSellIndex}
            onMissedGainsInfoClick={onMissedGainsInfoClick}
          />
        </>
      )}
    </PlanRecommendationSheetLayout>
  )
}
